/**
 * Profile Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';
import {
  fetchContractorProfile,
  fetchClientProfile,
  fetchProfileReviews,
  updateContractorProfile,
  updateContractorLocation,
  fetchMyContractorProfile,
  updateUserProfile,
  updateActorProfile,
  uploadAvatar,
  getMyPortfolio,
  createPortfolioItem,
  updatePortfolioItem,
  deletePortfolioItem,
  reorderPortfolio,
  getCategories,
  getCategoryTree,
  getMyCategories,
  assignCategories,
  removeCategory,
  getMyServices,
  createService,
  updateService,
  deleteService,
  getMyWorkingHours,
  updateWorkingHours,
  // Client profile API
  fetchMyClientProfile,
  updateClientProfile,
  updateClientLocation,
  // Certificates API
  getMyCertificates,
  uploadCertificate,
  deleteCertificate,
  // Social Media API
  getMySocialMediaLinks,
  updateSocialMediaLinks,
  deleteSocialMediaLink,
  // Auth API
  changePassword,
} from '../api';
import type {
  ClientProfileResponse,
  UpdateClientProfileDto,
  UpdateClientLocationDto,
} from '../api/clientProfile';
import { ordersApi, type OrderResponse } from '@/src/shared/api/orders.api';
import type { ContractorProfile } from '@hummii/shared-types';
import type { ClientProfile, ProfileReview } from '../types';
import type { UpdateContractorProfileDto, UpdateLocationDto } from '../api/updateProfile';
import type {
  UpdateUserProfileDto,
  UpdateActorDto,
  User,
  Actor,
  PortfolioItem,
  CreatePortfolioItemDto,
  UpdatePortfolioItemDto,
  ReorderPortfolioDto,
  Category,
  ContractorCategory,
  AssignCategoriesDto,
  Service,
  CreateServiceDto,
  UpdateServiceDto,
  WorkingHoursDay,
  UpdateWorkingHoursDto,
  Certificate,
  SocialMediaLink,
  SocialMediaLinkInput,
  ChangePasswordRequest,
} from '../api';

export interface UseContractorProfileOptions {
  userId: string;
  enabled?: boolean;
  useMock?: boolean;
}

export interface UseClientProfileOptions {
  userId: string;
  enabled?: boolean;
}

/**
 * Hook to fetch contractor profile
 */
export function useContractorProfile({ userId, enabled = true, useMock = false }: UseContractorProfileOptions) {
  return useQuery<ContractorProfile | null>({
    queryKey: ['contractor-profile', userId, useMock],
    queryFn: () => fetchContractorProfile({ userId }),
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch own contractor profile (uses /contractors/me)
 */
export function useMyContractorProfile(enabled = true, useMock = false) {
  return useQuery<ContractorProfile | null>({
    queryKey: ['my-contractor-profile', useMock],
    queryFn: () => fetchMyContractorProfile(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch client profile
 */
export function useClientProfile({ userId, enabled = true }: UseClientProfileOptions) {
  return useQuery<ClientProfile | null>({
    queryKey: ['client-profile', userId],
    queryFn: () => fetchClientProfile({ userId }),
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch profile reviews
 */
export function useProfileReviews(userId: string, enabled = true) {
  return useQuery<ProfileReview[]>({
    queryKey: ['profile-reviews', userId],
    queryFn: () => fetchProfileReviews(userId),
    enabled: enabled && !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to update contractor profile
 */
export function useUpdateContractorProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateContractorProfileDto) => updateContractorProfile(dto),
    onSuccess: (data) => {
      // Invalidate and refetch all contractor profile queries
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
      // Update the cache with the new data
      queryClient.setQueryData(['my-contractor-profile', false], data);
      if (data.userId) {
        queryClient.setQueryData(['contractor-profile', data.userId, false], data);
      }
    },
  });
}

/**
 * Hook to update contractor location
 */
export function useUpdateContractorLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateLocationDto) => updateContractorLocation(dto),
    onSuccess: (data) => {
      // Invalidate and refetch all contractor profile queries
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
      // Update the cache with the new data
      queryClient.setQueryData(['my-contractor-profile', false], data);
      if (data.userId) {
        queryClient.setQueryData(['contractor-profile', data.userId, false], data);
      }
    },
  });
}

/**
 * Hook to update user profile (name, phone)
 * @deprecated Use useUpdateActorProfile instead - profile data is now stored in Actor model
 */
export function useUpdateUserProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateUserProfileDto) => updateUserProfile(dto),
    onSuccess: () => {
      // Invalidate user and profile queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to update actor profile (name, phone, displayName)
 * Actor/Persona Architecture: Profile data is stored in Actor model
 */
export function useUpdateActorProfile() {
  const queryClient = useQueryClient();
  const { setActiveActor, availableActors, setAvailableActors } = useAuthStore();

  return useMutation({
    mutationFn: (dto: UpdateActorDto) => updateActorProfile(dto),
    onSuccess: (updatedActor) => {
      // Update active actor in auth store
      setActiveActor(updatedActor);

      // Update in available actors list too
      const updatedList = availableActors.map((actor) =>
        actor.id === updatedActor.id ? updatedActor : actor
      );
      setAvailableActors(updatedList);

      // Invalidate user, actor and profile queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['actors'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['client-profile'] });
    },
    onError: (error) => {
      console.error('[useUpdateActorProfile] Mutation error:', error);
    },
  });
}

/**
 * Hook to upload avatar
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();
  const { activeActor, setActiveActor, availableActors, setAvailableActors } = useAuthStore();

  return useMutation({
    mutationFn: (file: File) => uploadAvatar(file),
    onSuccess: (data: any) => {
      // Update active actor's avatar in auth store
      if (activeActor && data.avatarUrl) {
        const updatedActor = {
          ...activeActor,
          avatarUrl: data.avatarUrl,
          avatar: data.avatarUrl, // Legacy compatibility
        };
        
        setActiveActor(updatedActor);

        // Update in available actors list too
        const updatedList = availableActors.map((actor) =>
          actor.id === updatedActor.id ? updatedActor : actor
        );
        setAvailableActors(updatedList);
      }

      // Invalidate user and profile queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['actors'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['client-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-client-profile'] });
      // Invalidate orders so avatar shows up in order cards immediately
      queryClient.invalidateQueries({ queryKey: ['client-orders'] });
      queryClient.invalidateQueries({ queryKey: ['worker-orders'] });
    },
  });
}

/**
 * Hook to get my portfolio
 */
export function useMyPortfolio(enabled = true) {
  return useQuery<PortfolioItem[]>({
    queryKey: ['my-portfolio'],
    queryFn: getMyPortfolio,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create portfolio item
 */
export function useCreatePortfolioItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePortfolioItemDto) => createPortfolioItem(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to update portfolio item
 */
export function useUpdatePortfolioItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, dto }: { itemId: string; dto: UpdatePortfolioItemDto }) =>
      updatePortfolioItem(itemId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to delete portfolio item
 */
export function useDeletePortfolioItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => deletePortfolioItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to reorder portfolio
 */
export function useReorderPortfolio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: ReorderPortfolioDto) => reorderPortfolio(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to get all categories
 */
export function useCategories(enabled = true) {
  return useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: getCategories,
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes (categories don't change often)
  });
}

/**
 * Hook to get category tree
 */
export function useCategoryTree(enabled = true) {
  return useQuery<Category[]>({
    queryKey: ['category-tree'],
    queryFn: getCategoryTree,
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to get my assigned categories
 */
export function useMyCategories(enabled = true) {
  return useQuery<ContractorCategory[]>({
    queryKey: ['my-categories'],
    queryFn: getMyCategories,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to assign categories
 */
export function useAssignCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AssignCategoriesDto) => assignCategories(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-categories'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to remove category
 */
export function useRemoveCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryId: string) => removeCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-categories'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to get my services
 */
export function useMyServices(enabled = true) {
  return useQuery<Service[]>({
    queryKey: ['my-services'],
    queryFn: getMyServices,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create service
 */
export function useCreateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateServiceDto) => createService(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-services'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to update service
 */
export function useUpdateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ serviceId, dto }: { serviceId: string; dto: UpdateServiceDto }) =>
      updateService(serviceId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-services'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to delete service
 */
export function useDeleteService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (serviceId: string) => deleteService(serviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-services'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to fetch client's orders
 */
export function useClientOrders(enabled = true) {
  return useQuery<OrderResponse[]>({
    queryKey: ['client-orders'],
    queryFn: () => ordersApi.getMyOrders('client'),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to fetch worker's orders
 */
export function useWorkerOrders(enabled = true) {
  return useQuery<OrderResponse[]>({
    queryKey: ['worker-orders'],
    queryFn: () => ordersApi.getMyOrders('worker'),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to get my working hours
 */
export function useMyWorkingHours(enabled = true) {
  return useQuery<WorkingHoursDay[]>({
    queryKey: ['my-working-hours'],
    queryFn: getMyWorkingHours,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update working hours
 */
export function useUpdateWorkingHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateWorkingHoursDto) => updateWorkingHours(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-working-hours'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

// =========================================
// CLIENT PROFILE HOOKS
// =========================================

/**
 * Hook to fetch own client profile (uses /clients/me)
 * Similar to useMyContractorProfile but for CLIENT actor
 */
export function useMyClientProfile(enabled = true) {
  return useQuery<ClientProfileResponse | null>({
    queryKey: ['my-client-profile'],
    queryFn: async () => {
      try {
        return await fetchMyClientProfile();
      } catch (error) {
        console.error('[useMyClientProfile] Error fetching:', error);
        return null;
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once to avoid spam on 404
  });
}

/**
 * Hook to update client profile (bio, preferences, privacy settings)
 */
export function useUpdateClientProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateClientProfileDto) => updateClientProfile(dto),
    onSuccess: (data) => {
      // Invalidate client profile queries
      queryClient.invalidateQueries({ queryKey: ['my-client-profile'] });
      queryClient.invalidateQueries({ queryKey: ['client-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['actors'] });

      // Update the cache with the new data
      queryClient.setQueryData(['my-client-profile'], data);
    },
    onError: (error) => {
      console.error('[useUpdateClientProfile] Mutation error:', error);
    },
  });
}

/**
 * Hook to update client location
 */
export function useUpdateClientLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateClientLocationDto) => updateClientLocation(dto),
    onSuccess: (data) => {
      // Invalidate client profile queries
      queryClient.invalidateQueries({ queryKey: ['my-client-profile'] });
      queryClient.invalidateQueries({ queryKey: ['client-profile'] });

      // Update the cache with the new data
      queryClient.setQueryData(['my-client-profile'], data);
    },
    onError: (error) => {
      console.error('[useUpdateClientLocation] Mutation error:', error);
    },
  });
}

// =========================================
// CERTIFICATE HOOKS
// =========================================

/**
 * Hook to get my certificates
 */
export function useMyCertificates(enabled = true) {
  return useQuery<Certificate[]>({
    queryKey: ['my-certificates'],
    queryFn: getMyCertificates,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to upload certificate
 */
export function useUploadCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, title }: { file: File; title?: string }) =>
      uploadCertificate(file, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-certificates'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to delete certificate
 */
export function useDeleteCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteCertificate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-certificates'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

// =========================================
// SOCIAL MEDIA HOOKS
// =========================================

/**
 * Hook to get my social media links
 */
export function useMySocialMediaLinks(enabled = true) {
  return useQuery<SocialMediaLink[]>({
    queryKey: ['my-social-media'],
    queryFn: getMySocialMediaLinks,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update social media links (batch upsert)
 */
export function useUpdateSocialMediaLinks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (links: SocialMediaLinkInput[]) => updateSocialMediaLinks(links),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-social-media'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to delete social media link
 */
export function useDeleteSocialMediaLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteSocialMediaLink(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-social-media'] });
      queryClient.invalidateQueries({ queryKey: ['contractor-profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-contractor-profile'] });
    },
  });
}

/**
 * Hook to change password
 * Requires current password verification
 * Invalidates all sessions on backend after successful change
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => changePassword(data),
    // No need to invalidate queries since user will be logged out
  });
}
